import { useContext } from "react";
import { Segmented } from "antd";
import { 
    FaClipboardList, 
    FaCalendarDay, 
    FaTasks, 
    FaCheckCircle, 
    FaTimesCircle, 
    FaRegClock, 
    FaTruckPickup, 
    FaUserClock, 
    FaTruck 
} from "react-icons/fa";
import { HomeContext } from './HomeContext';
import styles from './Orders.module.css';

const tabsConfig = [
    {
        value: "pending_orders",
        label: "Pending Order",
        icon: FaRegClock,
        activeColors: { bg: "#fffbe6", border: "#ffe58f", text: "#d46b08" },
        inactiveColors: { icon: "#faad14", text: "#595959" }
    },
    {
        value: "ready_for_pickup",
        label: "Ready for Pickup",
        icon: FaTruckPickup,
        activeColors: { bg: "#e6fffb", border: "#87e8de", text: "#08979c" },
        inactiveColors: { icon: "#13c2c2", text: "#595959" }
    },
    {
        value: "waiting_driver_accept",
        label: "Waiting Driver Accept",
        icon: FaUserClock,
        activeColors: { bg: "#f9f0ff", border: "#d3adf7", text: "#531dab" },
        inactiveColors: { icon: "#722ed1", text: "#595959" }
    },
    {
        value: "today_orders",
        label: "Today Orders",
        icon: FaCalendarDay,
        activeColors: { bg: "#fff7e6", border: "#ffd591", text: "#d4380d" },
        inactiveColors: { icon: "#fa8c16", text: "#595959" }
    },
    {
        value: "assign_orders",
        label: "Assign Orders",
        icon: FaTasks,
        activeColors: { bg: "#f6ffed", border: "#b7eb8f", text: "#389e0d" },
        inactiveColors: { icon: "#52c41a", text: "#595959" }
    },
    {
        value: "out_for_delivery",
        label: "Out For Delivery",
        icon: FaTruck,
        activeColors: { bg: "#e6f7ff", border: "#91d5ff", text: "#096dd9" },
        inactiveColors: { icon: "#1890ff", text: "#595959" }
    },
    {
        value: "complete_orders",
        label: "Complete Orders",
        icon: FaCheckCircle,
        activeColors: { bg: "#fff0f6", border: "#ffadd2", text: "#c41d7f" },
        inactiveColors: { icon: "#eb2f96", text: "#595959" }
    },
    {
        value: "cancel_orders",
        label: "Cancel Orders",
        icon: FaTimesCircle,
        activeColors: { bg: "#fff1f0", border: "#ffa39e", text: "#cf1322" },
        inactiveColors: { icon: "#f5222d", text: "#595959" }
    },
    {
        value: "all_orders",
        label: "All Orders",
        icon: FaClipboardList,
        activeColors: { bg: "#f5f5f5", border: "#d9d9d9", text: "#262626" },
        inactiveColors: { icon: "#8c8c8c", text: "#595959" }
    }
];

const TabLabel = ({ active, label, icon: Icon, activeColors, inactiveColors }: {
    active: boolean;
    label: string;
    icon: any;
    activeColors: { bg: string; border: string; text: string };
    inactiveColors: { icon: string; text: string };
}) => {
    return (
        <div style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 14px",
            borderRadius: "8px",
            background: active ? activeColors.bg : "transparent",
            border: `1px solid ${active ? activeColors.border : "transparent"}`,
            color: active ? activeColors.text : inactiveColors.text,
            fontWeight: active ? 600 : 500,
            fontSize: "12px",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: active ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
            gap: "6px",
            cursor: "pointer",
            userSelect: "none"
        }}>
            <Icon style={{ 
                fontSize: "14px", 
                color: active ? activeColors.text : inactiveColors.icon,
                transition: "color 0.25s ease" 
            }} />
            <span>{label}</span>
        </div>
    );
};

const SegmentedButton = ({ activeButton: propActive, setActiveButton: propSetActive }: { activeButton?: string; setActiveButton?: (v: string) => void }) => {
    const context = useContext(HomeContext) as { activeButton?: string; setActiveButton?: (v: string) => void };
    const activeButton = propActive ?? context?.activeButton ?? 'all_orders';
    const setActiveButton = propSetActive ?? context?.setActiveButton;

    const options = tabsConfig.map(tab => ({
        value: tab.value,
        label: (
            <TabLabel
                active={activeButton === tab.value}
                label={tab.label}
                icon={tab.icon}
                activeColors={tab.activeColors}
                inactiveColors={tab.inactiveColors}
            />
        )
    }));

    return (
        <Segmented
            value={activeButton}
            options={options}
            onChange={(value) => setActiveButton?.(value as string)}
            className={styles.customSegmented}
        />
    );
};

export default SegmentedButton;
